import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Menu, Badge, Button, Space, Tag, Tooltip } from 'antd';
import {
  DashboardOutlined,
  ShoppingOutlined,
  UserOutlined,
  FileTextOutlined,
  CustomerServiceOutlined,
  TeamOutlined,
  ShopOutlined,
  CarOutlined,
  BarChartOutlined,
  MenuOutlined,
  PrinterOutlined,
  MessageOutlined,
  HistoryOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { API_BASE_URL, APP_VERSION } from '../../services/api';
import { CHANGELOG } from '../../changelog';

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
  isMobile?: boolean;
  onNavigate?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, isMobile, onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const go = (path: string) => {
    navigate(path);
    onNavigate?.();
  };
  
  // Счетчики уведомлений
  const [unansweredTickets, setUnansweredTickets] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [pendingEmployees, setPendingEmployees] = useState(0);
  const [pendingLinkRequests, setPendingLinkRequests] = useState(0);
  const [newOrders, setNewOrders] = useState(0);
  const [supplierResponses, setSupplierResponses] = useState(0);
  
  const { user } = useAuthStore();
  
  // Определяем эффективную роль на основе viewMode
  const viewMode = localStorage.getItem('viewMode') || 'own';
  const actualRole = user?.role || 'employee';
  const canSwitchView = ['customer', 'supplier', 'support'].includes(actualRole);
  const role = (viewMode === 'employee' && canSwitchView) ? 'employee' : actualRole;

  const labelNode = (text: string, badgeCount?: number) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span>{text}</span>
      {badgeCount && badgeCount > 0 ? (
        <Badge count={badgeCount} size="small" style={{ backgroundColor: '#f5222d' }} />
      ) : null}
    </span>
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !user) return;

    const fetchNotifications = async () => {
      try {
        // Для admin/support/customer_manager - необработанные тикеты
        if (role === 'admin' || role === 'support' || role === 'customer_manager') {
          const ticketsRes = await fetch(`${API_BASE_URL}/api/support/tickets`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (ticketsRes.ok) {
            const tickets = await ticketsRes.json();
            setUnansweredTickets(tickets.filter((t: any) => t.status === 'open').length);
          }
        }

        // Для admin - pending регистрации пользователей
        if (role === 'admin') {
          try {
            const countRes = await fetch(`${API_BASE_URL}/api/auth/pending-registrations/count`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (countRes.ok) {
              const data = await countRes.json();
              setPendingApprovals(data.count || 0);
            }
          } catch (error) {
            console.error('Failed to load pending registrations count:', error);
          }
        }

        // Для customer - pending регистрации сотрудников
        if (role === 'customer') {
          const approvalsRes = await fetch(`${API_BASE_URL}/api/auth/approvals`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (approvalsRes.ok) {
            const approvals = await approvalsRes.json();
            setPendingEmployees(approvals.filter((a: any) => a.status === 'pending').length);
          }

          // Ответы поставщиков на заявки (недавно одобренные/отклоненные)
          const linkReqRes = await fetch(`${API_BASE_URL}/api/link-requests/customer`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (linkReqRes.ok) {
            const requests = await linkReqRes.json();
            const recentResponses = requests.filter((r: any) => 
              r.status !== 'pending' && 
              r.respondedAt && 
              (new Date().getTime() - new Date(r.respondedAt).getTime()) < 24 * 60 * 60 * 1000 // последние 24 часа
            ).length;
            setSupplierResponses(recentResponses);
          }
        }

        // Для supplier - pending заявки от заказчиков
        if (role === 'supplier') {
          const linkReqRes = await fetch(`${API_BASE_URL}/api/link-requests/supplier`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (linkReqRes.ok) {
            const requests = await linkReqRes.json();
            setPendingLinkRequests(requests.filter((r: any) => r.status === 'pending').length);
          }

          // Новые заказы (статус pending)
          try {
            const newOrdersRes = await fetch(`${API_BASE_URL}/api/orders/new-count`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (newOrdersRes.ok) {
              const data = await newOrdersRes.json();
              setNewOrders(data.count || 0);
            }
          } catch (error) {
            console.error('Failed to load new orders count:', error);
          }
        }

      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Обновляем каждые 30 сек

    return () => clearInterval(interval);
  }, [role]);

  const latest = useMemo(() => CHANGELOG[0] || null, []);

  const getMenuItems = () => {
    const items: any[] = [];
    if (!user) return items;

    // Панель управления (только для admin)
    if (role === 'admin') {
      items.push({
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: labelNode('Панель управления'),
        onClick: () => go('/dashboard')
      });
      items.push({
        key: '/order-issues',
        icon: <MessageOutlined />,
        label: labelNode('Расхождения'),
        onClick: () => go('/order-issues')
      });
    }

    // Меню (сотрудник и контролёр — просмотр и заказы)
    if (role === 'employee' || role === 'customer_manager') {
      items.push({
        key: '/menu',
        icon: <MenuOutlined />,
        label: labelNode('Меню'),
        onClick: () => go('/menu')
      });
      items.push({
        key: '/my-order-history',
        icon: <HistoryOutlined />,
        label: labelNode('История заказов'),
        onClick: () => go('/my-order-history')
      });
      items.push({
        key: '/order-issues',
        icon: <MessageOutlined />,
        label: labelNode('Расхождения с заказом'),
        onClick: () => go('/order-issues')
      });
    }

    // Мое меню (supplier - редактирование)
    if (role === 'supplier') {
      items.push({
        key: '/my-menu',
        icon: <MenuOutlined />,
        label: labelNode('Мое меню'),
        onClick: () => go('/my-menu')
      });
    }

    // Заказы (supplier / customer / customer_manager)
    if (role === 'supplier' || role === 'customer' || role === 'customer_manager') {
      items.push({
        key: '/orders',
        icon: <ShoppingOutlined />,
        label: labelNode('Заказы'),
        onClick: () => go('/orders')
      });
      items.push({
        key: '/order-issues',
        icon: <MessageOutlined />,
        label: labelNode('Расхождения'),
        onClick: () => go('/order-issues')
      });
    }

    // Мои заказчики (supplier)
    if (role === 'supplier') {
      items.push({
        key: '/my-customers',
        icon: <TeamOutlined />,
        label: labelNode('Мои заказчики'),
        onClick: () => go('/my-customers')
      });
    }

    // Заявки от заказчиков (supplier)
    if (role === 'supplier') {
      items.push({
        key: '/org/link-requests',
        icon: <TeamOutlined />,
        label: labelNode('Заявки заказчиков', pendingLinkRequests),
        onClick: () => go('/org/link-requests')
      });
    }

    // Мои сотрудники (customer)
    if (role === 'customer') {
      items.push({
        key: '/users',
        icon: <TeamOutlined />,
        label: labelNode('Мои сотрудники', pendingEmployees),
        onClick: () => go('/users')
      });
    }

    // Мои поставщики (customer) + контролёр видит переключатель меню
    if (role === 'customer' || role === 'customer_manager') {
      items.push({
        key: '/my-suppliers',
        icon: <ShopOutlined />,
        label: labelNode('Мои поставщики', role === 'customer' ? supplierResponses : undefined),
        onClick: () => go('/my-suppliers')
      });
    }

    // Мои доставщики (customer и supplier)
    if (role === 'customer' || role === 'supplier') {
      items.push({
        key: '/my-delivery-team',
        icon: <CarOutlined />,
        label: labelNode('Мои доставщики'),
        onClick: () => go('/my-delivery-team')
      });
    }

    // Admin-only
    if (role === 'admin') {
      items.push(
        {
          key: '/users',
          icon: <TeamOutlined />,
          label: labelNode('Пользователи'),
          onClick: () => go('/users')
        },
        {
          key: '/admin/approvals',
          icon: <UserOutlined />,
          label: labelNode('Заявки на регистрацию', pendingApprovals),
          onClick: () => go('/admin/approvals')
        },
        {
          key: '/admin/documents',
          icon: <FileTextOutlined />,
          label: labelNode('Документы'),
          onClick: () => go('/admin/documents')
        },
      );
    }

    // Документооборот (все кроме employee)
    if (role !== 'employee') {
      items.push({
        key: '/documents',
        icon: <FileTextOutlined />,
        label: labelNode('Документооборот'),
        onClick: () => go('/documents')
      });
    }

    // Отчеты (заказчик / контролёр / админ)
    if (role === 'customer' || role === 'customer_manager' || role === 'admin') {
      items.push({
        key: '/reports',
        icon: <BarChartOutlined />,
        label: labelNode('Отчеты'),
        onClick: () => go('/reports')
      });
    }

    // Поддержка (всем пользователям, кроме admin/support)
    if (role !== 'admin' && role !== 'support') {
      items.push({
        key: '/support',
        icon: <CustomerServiceOutlined />,
        label: labelNode('Поддержка', role === 'customer_manager' ? unansweredTickets : undefined),
        onClick: () => go('/support')
      });
    }

    // Моя команда (только для admin)
    if (role === 'admin') {
      items.push({
        key: '/support-team',
        icon: <CustomerServiceOutlined />,
        label: labelNode('Моя команда'),
        onClick: () => go('/support-team')
      });
    }

    // Кабинет поддержки (admin/support) с бейджем
    if (role === 'admin' || role === 'support') {
      items.push({
        key: '/support-admin',
        icon: <CustomerServiceOutlined />,
        label: labelNode('Кабинет поддержки', unansweredTickets),
        onClick: () => go('/support-admin')
      });
    }

    // Адреса доставки (customer / customer_manager)
    if (role === 'customer' || role === 'customer_manager') {
      items.push({
        key: '/delivery-addresses',
        icon: <ShopOutlined />,
        label: labelNode('Адреса доставки'),
        onClick: () => go('/delivery-addresses')
      });
    }

    // Заказы по организациям (supplier)
    if (role === 'supplier') {
      items.push({
        key: '/supplier-orders',
        icon: <ShoppingOutlined />,
        label: labelNode('Заказы организаций', newOrders),
        onClick: () => go('/supplier-orders')
      });
    }

    // Аналитика оценок (supplier)
    if (role === 'supplier') {
      items.push({
        key: '/dish-ratings',
        icon: <BarChartOutlined />,
        label: labelNode('Оценки блюд'),
        onClick: () => go('/dish-ratings')
      });
    }

    // Термопечать (supplier)
    if (role === 'supplier') {
      items.push({
        key: '/thermal-printing',
        icon: <PrinterOutlined />,
        label: labelNode('Термопечать'),
        onClick: () => go('/thermal-printing')
      });
    }

    // ЛК доставщика (delivery)
    if (role === 'delivery') {
      items.push(
        {
          key: '/orders',
          icon: <ShoppingOutlined />,
          label: labelNode('Заказы к доставке'),
          onClick: () => go('/orders')
        },
        {
          key: '/delivery-dashboard',
          icon: <CarOutlined />,
          label: labelNode('Мои доставки'),
          onClick: () => go('/delivery-dashboard')
        }
      );
    }

    // Профиль для всех
    items.push({
      key: '/profile',
      icon: <UserOutlined />,
      label: labelNode('Профиль'),
      onClick: () => go('/profile')
    });

    // Инструкция для всех
    items.push({
      key: '/guide',
      icon: <QuestionCircleOutlined />,
      label: labelNode('Инструкция'),
      onClick: () => go('/guide')
    });

    return items;
  };

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      trigger={null}
      breakpoint={undefined}
      width={250}
      collapsedWidth={isMobile ? 0 : 80}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: isMobile && !collapsed ? 1002 : 1000,
        boxShadow: isMobile && !collapsed ? '0 8px 24px rgba(0,0,0,0.25)' : undefined,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            color: '#fff',
            fontSize: collapsed ? 24 : 18,
            fontWeight: 'bold',
            padding: collapsed ? '0' : '0 24px',
          }}
        >
          {collapsed ? 'L' : (
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)', marginBottom: 2, fontWeight: 600, letterSpacing: 0.2 }}>
                Lunch ordering
              </div>
              <div style={{ fontSize: '18px', fontWeight: 800, letterSpacing: 0.2 }}>Luemen</div>
            </div>
          )}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          style={{
            flex: 1,
            borderRight: 0,
            textAlign: 'left',
            padding: 8,
            background: 'transparent',
            overflow: 'auto',
          }}
          items={getMenuItems()}
        />

        <div
          style={{
            padding: collapsed ? '10px 8px' : '10px 12px',
            borderTop: '1px solid rgba(255,255,255,0.12)',
            background: 'rgba(0,0,0,0.12)',
          }}
        >
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Tooltip title={latest ? `Последнее: ${latest.version} (${latest.date})` : undefined} placement="right">
              <Button
                type="text"
                onClick={() => go('/changelog')}
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  padding: 0,
                  height: 'auto',
                  textAlign: 'left',
                }}
                block={!collapsed}
              >
                {collapsed ? (
                  <Tag color="blue" style={{ margin: 0 }}>
                    v{APP_VERSION}
                  </Tag>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, opacity: 0.9 }}>Версия</span>
                    <Tag color="blue" style={{ margin: 0 }}>
                      v{APP_VERSION}
                    </Tag>
                  </span>
                )}
              </Button>
            </Tooltip>
          </Space>
        </div>
      </div>
    </Sider>
  );
};

export default Sidebar;

